import { Injectable } from '@angular/core';
import { AuthService } from '../auth-service/auth.service';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private inFlightRefresh: Promise<string | null> | null = null;

  constructor(private authService: AuthService) { }

  /** Returns a valid token. Refreshes once if expired. */
  public async getTokenAsync(): Promise<string | null> {
    const expired = await this.authService.tokenExpired();
    if (!expired) {
      return this.authService.token();
    }
    return this.refreshTokenAsync(); // share single-flight below
  }

  /** Refreshes the token if (and only if) expired, single-flight. */
  public async refreshTokenAsync(): Promise<string | null> {
    // If not expired anymore (another code path already refreshed), just return.
    if (!(await this.authService.tokenExpired())) {
      return this.authService.token();
    }

    // Single-flight: reuse the same promise while a refresh is running.
    if (!this.inFlightRefresh) {
      this.inFlightRefresh = (async () => {
        try {
          const user = await this.authService.token(); // quick check: user still logged in?
          if (!user && !(await this.authService.tokenExpired())) {
            return this.authService.token();
          }
          await this.authService.refreshToken(); // performs semaphore-protected refresh
          const refreshedToken = await this.authService.token();
          if (!refreshedToken || (await this.authService.tokenExpired())) {
            return null;
          }
          return refreshedToken;
        } finally {
          this.inFlightRefresh = null;
        }
      })();
    }
    return this.inFlightRefresh;
  }

  public async tokenExpired(): Promise<boolean> {
    return this.authService.tokenExpired();
  }

  public async logout(reason: string) {
    await this.authService.logout('token service logout ' + reason);
  }

  public async logDebug(msg: string) {
    await this.authService.logDebug(msg);
  }
}